import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm, FormProvider } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { validarDocumento } from '../utils/validadores'
import { v4 as uuid } from 'uuid'
import { ProgressBar } from '../components/common/ProgressBar'
import { StepCliente } from '../components/form/StepCliente'
import { StepMoveis } from '../components/form/StepMoveis'
import { StepResumo } from '../components/form/StepResumo'
import { savePedido, getNextNumeroPedido, getConfig } from '../utils/storage'
import { sincronizarComGoogleSheets } from '../utils/googleSheets'
import { STATUS_DEFAULT } from '../data/kanban'
import { ChevronLeft, ChevronRight, Save } from 'lucide-react'

function add17DiasUteis(dataStr) {
  if (!dataStr) return ''
  const dt = new Date(dataStr + 'T12:00:00')
  let count = 0
  while (count < 17) {
    dt.setDate(dt.getDate() + 1)
    const dow = dt.getDay()
    if (dow !== 0 && dow !== 6) count++
  }
  return dt.toISOString().split('T')[0]
}

// Validação do cliente como ZodObject (não usa superRefine no schemaCliente
// pra preservar a chamada schemaCliente.merge(schemaMoveis) lá embaixo).
// O superRefine vai no objeto cliente, que ainda é ZodObject.
const clienteSchema = z.object({
  nome:            z.string().min(2, 'Nome obrigatório'),
  contato:         z.string().min(8, 'Contato obrigatório'),
  email:           z.string().email('E-mail inválido').or(z.literal('')),
  rua:             z.string().min(3, 'Rua obrigatória'),
  numero:          z.string().min(1, 'Número obrigatório'),
  bairro:          z.string().min(2, 'Bairro obrigatório'),
  cidade:          z.string().min(2, 'Cidade obrigatória'),
  uf:              z.string().optional(),
  cep:             z.string().optional(),
  complemento:     z.string().min(1, 'Complemento obrigatório'),
  tipoDocumento:   z.enum(['CPF', 'CNPJ']),
  documento:       z.string().min(11, 'Documento obrigatório'),
  contribuinteICMS:z.string().nullable().optional(),
}).superRefine((c, ctx) => {
  // Valida dígito verificador. Antes só validava tamanho, o que deixava
  // passar CPF/CNPJ inválido e o Bling rejeitava depois com mensagem
  // genérica "Não foi possível salvar o contato".
  if (c.documento && !validarDocumento(c.documento, c.tipoDocumento)) {
    ctx.addIssue({
      code:    z.ZodIssueCode.custom,
      path:    ['documento'],
      message: `${c.tipoDocumento} inválido — confira os dígitos`,
    })
  }
})

const schemaCliente = z.object({
  dataVenda: z.string().min(1, 'Informe a data da venda'),
  cliente:   clienteSchema,
  // Frete obrigatório — força a vendedora a informar o valor (ou "0" pra
  // grátis). Antes ficava vazio por padrão e passava despercebido.
  frete:     z.string().min(1, 'Informe o valor do frete (digite 0 se for grátis)'),
})

const schemaMoveis = z.object({
  prazo:  z.string().min(1, 'Informe o prazo de entrega'),
  moveis: z.array(z.any()).min(1, 'Adicione pelo menos um móvel'),
})

const schemaFull = schemaCliente.merge(schemaMoveis).extend({
  observacoesGerais: z.string().optional(),
})

const STEPS = [
  { id: 1, schema: schemaCliente },
  { id: 2, schema: schemaMoveis  },
  { id: 3, schema: null          },
]

export function NewOrder() {
  const [step, setStep]       = useState(1)
  const [saving, setSaving]   = useState(false)
  const navigate              = useNavigate()

  const methods = useForm({
    resolver: zodResolver(step < 3 ? STEPS[step - 1].schema : schemaFull),
    defaultValues: {
      cliente: {
        nome:            '',
        contato:         '',
        email:           '',
        rua:             '',
        numero:          '',
        bairro:          '',
        cidade:          '',
        uf:              '',
        cep:             '',
        complemento:     '',
        tipoDocumento:   'CPF',
        documento:       '',
        contribuinteICMS:null,
      },
      dataVenda:              new Date().toISOString().split('T')[0],
      vendedora:              '',
      formaPagamento:         '',
      prazoPagamento:         '',
      prazoPagamentoCustom:   '',
      // Split de pagamento em 2 formas (entrada + saldo). Se dividido=false,
      // usa formaPagamento único (legado). Se true, usa entrada/saldo abaixo.
      pagamentoDividido:      false,
      entradaValor:           '',
      entradaForma:           '',
      entradaObs:             '',
      saldoValor:             '',
      saldoForma:             '',
      saldoObs:               '',
      prazo:                  add17DiasUteis(new Date().toISOString().split('T')[0]),
      moveis:                 [],
      observacoesGerais:      '',
      precoFinal:             '',
      freteLocal:             '',
      tags:                   [],
    },
    mode: 'onTouched',
  })

  const watchedDataVenda = methods.watch('dataVenda')
  useEffect(() => {
    const novoPrazo = add17DiasUteis(watchedDataVenda)
    if (novoPrazo) methods.setValue('prazo', novoPrazo, { shouldValidate: false })
  }, [watchedDataVenda])

  async function handleNext() {
    const valid = await methods.trigger(
      step === 1
        ? ['dataVenda','cliente.nome','cliente.contato','cliente.rua','cliente.numero','cliente.bairro','cliente.cidade','cliente.tipoDocumento','cliente.documento','cliente.complemento']
        : ['prazo','moveis']
    )
    if (valid) setStep(s => s + 1)
  }

  async function handleSalvar() {
    const valid = await methods.trigger()
    if (!valid) return

    setSaving(true)
    const values = methods.getValues()
    const totalBruto = values.moveis.reduce((s, m) => s + Number(m.preco || 0), 0)

    // Desconto
    const precoFinalNum = values.precoFinal
      ? Number(String(values.precoFinal).replace(/\./g,'').replace(',','.'))
      : 0
    const temDesconto   = precoFinalNum > 0 && precoFinalNum < totalBruto
    const fator         = temDesconto ? precoFinalNum / totalBruto : 1
    const desconto      = temDesconto ? totalBruto - precoFinalNum : 0
    const total         = temDesconto ? precoFinalNum : totalBruto

    // Aplica desconto proporcional a cada item
    const moveis = values.moveis.map(m => {
      const precoOriginal = Number(m.preco || 0)
      return {
        ...m,
        precoOriginal,
        preco: temDesconto ? Math.round(precoOriginal * fator * 100) / 100 : precoOriginal,
      }
    })

    const c = values.cliente
    const enderecoFormatado = [
      `${c.rua}, ${c.numero}`,
      c.complemento || null,
      c.bairro,
      c.cidade,
    ].filter(Boolean).join(', ')

    const pedido = {
      id:                   uuid(),
      numero:               getNextNumeroPedido(),
      criadoEm:             new Date().toISOString(),
      dataVenda:            values.dataVenda,
      status:               STATUS_DEFAULT,
      prazo:                values.prazo,
      // Prazo IMUTÁVEL do cadastro — usado pra detectar atraso real
      // (comparação contra o original, não contra o último valor). Sem
      // isso, gerente que adianta e depois volta pro prazo original
      // fica marcado como atrasado por engano.
      prazoCadastro:        values.prazo,
      cliente:              { ...c, endereco: enderecoFormatado },
      vendedora:              values.vendedora || '',
      formaPagamento:         values.formaPagamento || '',
      prazoPagamento:         values.prazoPagamento || '',
      prazoPagamentoCustom:   values.prazoPagamentoCustom || '',
      valorPendenteEntrega:   values.valorPendenteEntrega ? Number(String(values.valorPendenteEntrega).replace(/\./g,'').replace(',','.')) : 0,
      // Split de pagamento — só grava se pagamentoDividido = true. Senão,
      // formaPagamento acima é a fonte da verdade (pagamento único).
      pagamento:              values.pagamentoDividido ? {
        dividido: true,
        entrada:  {
          valor: values.entradaValor ? Number(String(values.entradaValor).replace(/\./g,'').replace(',','.')) : 0,
          forma: values.entradaForma || '',
          obs:   values.entradaObs   || '',
        },
        saldo:    {
          valor: values.saldoValor ? Number(String(values.saldoValor).replace(/\./g,'').replace(',','.')) : 0,
          forma: values.saldoForma || '',
          obs:   values.saldoObs   || '',
        },
      } : null,
      frete:                  values.frete ? Number(String(values.frete).replace(/\./g,'').replace(',','.')) : 0,
      freteLocal:             values.freteLocal || '',
      moveis,
      valorTotal:           total,
      desconto,
      observacoesGerais:    values.observacoesGerais || '',
      tags:                 [],
    }

    savePedido(pedido)
    sincronizarComGoogleSheets(pedido)
    // Integração Bling: feita manualmente pelo botão "Enviar agora" na tela do pedido

    setSaving(false)
    navigate(`/pedidos/${pedido.id}`, { state: { novo: true } })
  }

  const stepComponents = [<StepCliente />, <StepMoveis />, <StepResumo />]

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-xl font-bold text-slate-800">Novo pedido</h1>
        <p className="text-sm text-slate-400 mt-1">Preencha os dados do cliente e os móveis solicitados</p>
      </div>

      <div className="mb-8 px-4">
        <ProgressBar current={step} />
      </div>

      <FormProvider {...methods}>
        <form onSubmit={e => e.preventDefault()}>
          <div className="card p-6 mb-6">
            {stepComponents[step - 1]}
          </div>

          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => step > 1 ? setStep(s => s - 1) : navigate(-1)}
              className="btn-secondary"
            >
              <ChevronLeft size={16} />
              {step > 1 ? 'Anterior' : 'Cancelar'}
            </button>

            {step < 3 ? (
              <button type="button" onClick={handleNext} className="btn-primary">
                Próximo
                <ChevronRight size={16} />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSalvar}
                disabled={saving}
                className="btn-primary"
              >
                <Save size={16} />
                {saving ? 'Salvando...' : 'Salvar pedido'}
              </button>
            )}
          </div>
        </form>
      </FormProvider>
    </div>
  )
}
